import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap, Subject, switchMap } from 'rxjs';
import { GoogleSignInService } from './google-sign-in.service';
import { environment } from '../environment/environment';
import type {
  User,
  RegisterUserRequestDto,
  LoginRequestDto,
  CompleteRegistrationPayload,
  SSOResponse,
  SSOProvider,
} from '../models';

/** Session token storage key */
export const SESSION_TOKEN_KEY = 'lumio_session_token';

/** Refresh token storage key */
export const REFRESH_TOKEN_KEY = 'lumio_refresh_token';

/** Flag set when user logs in via Google (skip password step in completion flow). */
export const AUTH_VIA_GOOGLE_KEY = 'lumio_auth_via_google';

/** Response shape from register/complete-registration endpoints */
interface AuthResponse {
  sessionToken?: string;
  user?: User;
}

/** Response shape from token endpoints that include a refresh token */
interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

/** Base URL for auth endpoints — resolves to {host}/api, matching the /api/auth/* backend routes. */
const AUTH_BASE = environment.urls.backend.gateway;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly googleSignIn = inject(GoogleSignInService);
  private readonly _logout$ = new Subject<void>();
  /** Emitido ao fazer logout — para outros serviços limparem cache. */
  readonly logout$ = this._logout$.asObservable();

  /**
   * Register a new user (POST /auth/register).
   * Creates account with email, plan, password. Does not return a token - call login() after.
   */
  register(payload: RegisterUserRequestDto): Observable<void> {
    return this.http.post<void>(`${AUTH_BASE}/auth/register`, payload);
  }

  /**
   * Authenticate with email/password (POST /auth/token).
   * Returns AuthResponseDto {accessToken, refreshToken}. Stores both tokens automatically.
   */
  login(payload: LoginRequestDto): Observable<string> {
    return this.http
      .post<TokenResponse>(`${AUTH_BASE}/auth/token`, payload)
      .pipe(
        tap(({ accessToken, refreshToken }) => {
          this.setSessionToken(accessToken);
          this.setRefreshToken(refreshToken);
          this.clearGoogleAuthFlag();
        }),
        map(({ accessToken }) => accessToken),
      );
  }

  /**
   * Authenticate via Google ID token (POST /auth/oidc/google).
   * Returns AuthResponseDto {accessToken, refreshToken}. Stores both tokens automatically.
   */
  googleLogin(idToken: string): Observable<string> {
    return this.http
      .post<TokenResponse>(`${AUTH_BASE}/auth/oidc/google`, { idToken })
      .pipe(
        tap(({ accessToken, refreshToken }) => {
          this.setSessionToken(accessToken);
          this.setRefreshToken(refreshToken);
          this.setGoogleAuthFlag();
        }),
        map(({ accessToken }) => accessToken),
      );
  }

  /**
   * Rotate refresh token and get a new token pair (POST /auth/refresh).
   * Stores the new access and refresh tokens automatically.
   */
  refreshAccessToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    return this.http
      .post<TokenResponse>(`${AUTH_BASE}/auth/refresh`, { refreshToken })
      .pipe(
        tap(({ accessToken, refreshToken: newRefresh }) => {
          this.setSessionToken(accessToken);
          this.setRefreshToken(newRefresh);
        }),
        map(({ accessToken }) => accessToken),
      );
  }

  /**
   * @deprecated Use register() + login() or googleLogin() for the new API.
   * SSO auth flow (Google, Apple, Facebook) - legacy endpoint.
   */
  sso(provider: SSOProvider, token: string): Observable<SSOResponse> {
    return this.http
      .post<SSOResponse>(`${AUTH_BASE}/auth/sso/${provider}`, { token })
      .pipe(
        tap((res) => {
          if (res.sessionToken) {
            this.setSessionToken(res.sessionToken);
          }
        })
      );
  }

  /**
   * @deprecated Use UserService.upsertProfile() for the new API.
   * Complete registration after SSO (when provider didn't return all fields)
   */
  completeRegistration(payload: CompleteRegistrationPayload): Observable<User> {
    return this.http
      .post<AuthResponse | User>(
        `${AUTH_BASE}/auth/complete-registration`,
        payload
      )
      .pipe(
        tap((res) => {
          const token =
            typeof res === 'object' && 'sessionToken' in res
              ? (res as AuthResponse).sessionToken
              : undefined;
          if (token) this.setSessionToken(token);
        }),
        map((res) =>
          typeof res === 'object' && 'user' in res
            ? (res as AuthResponse).user!
            : (res as User)
        )
      );
  }

  /**
   * Request a password reset e-mail (POST /auth/forgot-password).
   * Always resolves — backend never reveals if the e-mail is registered.
   * Captcha-protected (action = forgot_password) in dev/prod environments.
   */
  forgotPassword(email: string, captchaToken?: string): Observable<void> {
    const body: Record<string, string> = { email };
    if (captchaToken) body['captchaToken'] = captchaToken;
    return this.http.post<void>(`${AUTH_BASE}/auth/forgot-password`, body);
  }

  /**
   * Reset password using the token from the e-mail link (POST /auth/reset-password).
   * Throws 400/410 when the token is expired or already used.
   */
  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${AUTH_BASE}/auth/reset-password`, { token, password: newPassword });
  }

  /**
   * Submit the 6-digit code from the verification email (POST /auth/verify-email).
   * Requires a valid JWT (user must be logged in). Codes expire after 30 min / 5 attempts.
   */
  verifyEmail(code: string): Observable<void> {
    return this.http.post<void>(`${AUTH_BASE}/auth/verify-email`, { code });
  }

  /**
   * Invalidate the current unused code and send a new verification email (POST /auth/resend-verification).
   * Backend enforces a cooldown — throws EMAIL_VERIFICATION_RESEND_COOLDOWN if called too soon.
   */
  resendVerification(): Observable<void> {
    return this.http.post<void>(`${AUTH_BASE}/auth/resend-verification`, {});
  }

  /**
   * Request OTP to be sent via SMS to the given phone number.
   * Call this when user submits the phone verification form.
   */
  requestPhoneOtp(phone: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${AUTH_BASE}/auth/phone/otp/request`,
      { phone }
    );
  }

  /**
   * Verify the OTP code entered by the user.
   */
  verifyPhoneOtp(phone: string, otp: string): Observable<AuthResponse | User> {
    return this.http
      .post<AuthResponse | User>(`${AUTH_BASE}/auth/phone/otp/verify`, {
        phone,
        otp,
      })
      .pipe(
        tap((res) => {
          const token =
            typeof res === 'object' && 'sessionToken' in res
              ? (res as AuthResponse).sessionToken
              : undefined;
          if (token) this.setSessionToken(token);
        })
      );
  }

  /**
   * Get current user (after login)
   */
  getMe(): Observable<User | null> {
    const token = this.getSessionToken();
    if (!token) {
      return of(null);
    }
    return this.http.get<User>(`${AUTH_BASE}/auth/me`).pipe(
      catchError(() => of(null))
    );
  }

  setSessionToken(token: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    }
  }

  getSessionToken(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(SESSION_TOKEN_KEY);
    }
    return null;
  }

  setRefreshToken(token: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }
  }

  getRefreshToken(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    }
    return null;
  }

  /**
   * Invalidate the refresh token server-side, then clear all local auth state.
   * Always clears local state even if the server call fails.
   */
  logout(): void {
    const clear = () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(SESSION_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        this.clearGoogleAuthFlag();
      }
      void this.googleSignIn.signOut();
      this._logout$.next();
    };

    if (this.getSessionToken()) {
      this.http
        .post(`${AUTH_BASE}/auth/logout`, {})
        .pipe(catchError(() => of(null)))
        .subscribe(() => clear());
    } else {
      clear();
    }
  }

  /**
   * Clears local auth state (tokens, flags) without calling the server.
   * Use this when the session is already known to be invalid (e.g. after
   * the interceptor failed to refresh) so we don't make a redundant server call.
   */
  clearLocalSession(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      this.clearGoogleAuthFlag();
    }
    void this.googleSignIn.signOut();
    this._logout$.next();
  }

  /**
   * Wipes all localStorage entries EXCEPT user preferences that must survive
   * across sessions (theme, onboarding completion flag). Also clears sessionStorage.
   * Call this when the user navigates to login / register / onboarding so they
   * always start with a clean slate.
   */
  clearForNewSession(): void {
    if (typeof localStorage !== 'undefined') {
      const keep = new Set(['lumio-theme', 'lumio_onboarding_completed', 'lumio_cookie_consent']);
      Object.keys(localStorage)
        .filter((k) => !keep.has(k))
        .forEach((k) => localStorage.removeItem(k));
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
  }

  isAuthenticated(): boolean {
    return !!this.getSessionToken();
  }

  /** Indica se o usuário fez login/registro via Google. Usado para pular a tela de senha. */
  isGoogleAuth(): boolean {
    if (typeof localStorage === 'undefined') return false;
    return (
      !!this.getSessionToken() &&
      localStorage.getItem(AUTH_VIA_GOOGLE_KEY) === 'true'
    );
  }

  private setGoogleAuthFlag(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(AUTH_VIA_GOOGLE_KEY, 'true');
    }
  }

  private clearGoogleAuthFlag(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(AUTH_VIA_GOOGLE_KEY);
    }
  }
}
